import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AudioToggleButton, LocalVideo, SettingsToggleButton, VideoToggleButton } from '@/components';
import { useClientHeight } from '@/hooks/useClientHeight';
import { useIsOnline } from '@/hooks/useIsOnline';
import { api } from '@/services/api';
import { call } from '@/services/call';
import { logger } from '@/services/logger';
import { selectAppointmentIsAppointment } from '@/store/features/appointment/selectors';
import { selectSettingsPermissionsMicrophone } from '@/store/features/settings/selectors';
import { showUIAlert } from '@/store/features/ui/slice';
import { AlertTypes } from '@/store/features/ui/types';
import { selectUserData } from '@/store/features/user/selectors';
import { setUserName, toggleLocalAudioEnabled } from '@/store/features/user/slice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Input } from '@/ui';
import { isDoctorStorage, twilioTokenStorage, userIdStorage } from '@/utils/storage';
import {
  PageWrapper,
  Header,
  Logo,
  Content,
  Container,
  VideoWrapper,
  ControlsOverlay,
  InputGroup,
  JoinButton
} from './styled';

function Lobby() {
  const height = useClientHeight();

  const { id } = useParams<{ id: string }>();

  const { t } = useTranslation();

  const isOnline = useIsOnline();

  const [isInitialized, setInitialized] = useState(false);

  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUserData);
  const isAppointment = useAppSelector(selectAppointmentIsAppointment);
  const microphoneStatus = useAppSelector(selectSettingsPermissionsMicrophone);
  const prevMicrophoneStatus = useRef(microphoneStatus);

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      name: user?.name || '',
      password: '',
    },
    mode: 'onChange',
  });

  const [loading, setLoading] = useState(false);

  const getMedia = useCallback(async (tryOnlyAudio = false) => {
    try {
      if (tryOnlyAudio) {
        await call.init(
          false,
          true,
        );
      } else {
        await call.init();
      }

      dispatch(toggleLocalAudioEnabled(true));
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case 'Permission denied':
            if (!tryOnlyAudio) {
              getMedia(true);
            }
            break;
          default:
          case 'Failed to attach':
            logger.error(error);
            break;
        }
      } else {
        logger.error(error);
      }
    } finally {
      setTimeout(() => {
        setInitialized(true);
      }, 100);
    }
  }, [dispatch]);

  useEffect(() => {
    getMedia();
  }, [getMedia]);

  useEffect(() => {
    if (
      prevMicrophoneStatus.current !== 'granted'
      && prevMicrophoneStatus.current !== 'pending'
      && microphoneStatus === 'granted'
      && !call.audioTrack
    ) {
      setInitialized(false);
      getMedia(!call.videoTrack);
    }

    prevMicrophoneStatus.current = microphoneStatus;
  }, [getMedia, microphoneStatus]);

  const showInputs = !user || !isAppointment;

  const onJoin = async (values: Record<string, string>) => {
    try {
      if (microphoneStatus !== 'granted') {
        dispatch(
          showUIAlert({
            title: t('microphone_access_needed'),
            message: t('microphone_access_message'),
            type: AlertTypes.error,
          }),
        );
        return;
      }

      setLoading(true);

      const res = await api.post("rooms/join", {
        roomId: id,
        identity: userIdStorage.getItem(),
        password: values.password,
        isDoctor: isDoctorStorage.getItem() === '1',
      });

      twilioTokenStorage.setItem(res.data.data);
      dispatch(setUserName(values.name));

      navigate(`/room/${id}`);
    } catch (error) {
      logger.error(error);
      methods.setError('password', {
        type: "custom",
        message: t('password_invalid'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper $height={height}>
      <Header>
        <Logo />
      </Header>
      <Content>
        <Container>
          <VideoWrapper>
            <LocalVideo />
            {isInitialized && (
              <ControlsOverlay>
                <AudioToggleButton />
                <VideoToggleButton />
                <SettingsToggleButton />
              </ControlsOverlay>
            )}
          </VideoWrapper>
          <form onSubmit={methods.handleSubmit(onJoin)}>
            {showInputs && (
              <InputGroup>
                <Controller
                  render={({ field }) => (
                    <Input
                      placeholder={t('your_name')}
                      onChange={field.onChange}
                      value={field.value}
                      maxLength={30}
                      disabled={loading}
                      autoComplete="name"
                    />
                  )}
                  name="name"
                  control={methods.control}
                  rules={{ required: false }}
                />
                <Controller
                  render={({ field, fieldState }) => (
                    <Input
                      type="password"
                      placeholder={t('room_password')}
                      onChange={field.onChange}
                      value={field.value}
                      error={fieldState.error?.message}
                      maxLength={255}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  )}
                  name="password"
                  control={methods.control}
                  rules={{ required: t('password_required') }}
                />
              </InputGroup>
            )}
            <JoinButton disabled={loading || !isInitialized || !isOnline} type="submit">
              {t('join_now')}
            </JoinButton>
          </form>
        </Container>
      </Content>
    </PageWrapper>
  );
}

export default Lobby;
